/* P.Louise — Skincare routine bundle builder
 * Build-your-own-bundle from a collection with tiered pricing
 * (e.g. 3 for £27, 4 for £35, 5 for £42), a one-time / subscribe
 * (Recharge selling plan) toggle, and a Shopify cart/add.js checkout.
 */
(function () {
  const SVG_X = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>';

  function formatMoney(cents, format) {
    const value = (cents / 100).toFixed(2);
    const [whole, decimal] = value.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = (format || '£{{amount}}').match(placeholder);
    const key = match ? match[1] : 'amount';
    const amount = key === 'amount_no_decimals' ? withCommas
      : key === 'amount_with_comma_separator' ? `${withCommas},${decimal}`
      : key === 'amount_no_decimals_with_comma_separator' ? withCommas
      : `${withCommas}.${decimal}`;
    return (format || '£{{amount}}').replace(placeholder, amount);
  }

  class SkincareBundleBuilder {
    constructor(root) {
      this.root = root;
      this.money = (c) => formatMoney(c, root.dataset.moneyFormat);

      // Tiers: parallel size/price arrays → sorted [{size, price(pence)}].
      // Prices arrive in pounds from the section settings, so convert to
      // pence to match Shopify's money (variant.price) and formatMoney.
      const sizes = (root.dataset.tierSizes || '').split(',').filter(Boolean).map(Number);
      const prices = (root.dataset.tierPrices || '').split(',').filter(Boolean).map(Number);
      this.tiers = sizes
        .map((size, i) => ({ size, price: Math.round((prices[i] || 0) * 100) }))
        .filter((t) => t.size > 0)
        .sort((a, b) => a.size - b.size);
      this.minSize = this.tiers.length ? this.tiers[0].size : 0;
      this.maxSize = parseInt(root.dataset.maxSize, 10) || (this.tiers.length ? this.tiers[this.tiers.length - 1].size : 0);

      this.subscriptionEnabled = root.dataset.subscription === 'true';
      this.subscribeDiscount = parseFloat(root.dataset.subscribeDiscount) || 0;
      this.purchaseType = 'onetime';

      // Elements
      this.search = root.querySelector('[data-sbb-search]');
      this.chipsRow = root.querySelector('[data-sbb-chips]');
      this.chipPrev = root.querySelector('[data-sbb-chip-prev]');
      this.chipNext = root.querySelector('[data-sbb-chip-next]');
      this.grid = root.querySelector('[data-sbb-grid]');
      this.cards = Array.from(root.querySelectorAll('[data-sbb-card]'));
      this.empty = root.querySelector('[data-sbb-empty]');
      this.countline = root.querySelector('[data-sbb-countline]');
      this.clearBtn = root.querySelector('[data-sbb-clear]');

      this.tierEls = Array.from(root.querySelectorAll('[data-sbb-tier]'));
      this.progressFill = root.querySelector('[data-sbb-progress-fill]');
      this.progressLabel = root.querySelector('[data-sbb-progress-label]');
      this.traySlots = Array.from(root.querySelectorAll('[data-sbb-tray-slot]'));

      this.purchaseRow = root.querySelector('[data-sbb-purchase]');
      this.purchaseOpts = Array.from(root.querySelectorAll('[data-sbb-purchase-opt]'));

      this.picksEl = root.querySelector('[data-sbb-picks]');
      this.placeholders = Array.from(root.querySelectorAll('[data-sbb-placeholder]'));
      this.fullPriceEl = root.querySelector('[data-sbb-fullprice]');
      this.priceEl = root.querySelector('[data-sbb-price]');
      this.saveEl = root.querySelector('[data-sbb-save]');
      this.cta = root.querySelector('[data-sbb-cta]');
      this.panel = root.querySelector('[data-sbb-panel]');
      this.peekToggle = root.querySelector('[data-sbb-peek-toggle]');
      this.peekCount = root.querySelector('[data-sbb-peek-count]');
      this.peekCta = root.querySelector('[data-sbb-peek-cta]');

      this.layout = root.querySelector('.sbb-layout');
      this.side = root.querySelector('.sbb-side');

      this.toast = root.querySelector('[data-sbb-toast]');
      this.toastInner = root.querySelector('[data-sbb-toast-inner]');
      this.accordionTriggers = Array.from(root.querySelectorAll('[data-sbb-accordion-trigger]'));

      this.query = '';
      this.category = 'All';
      this.picks = []; // array of variant id strings, duplicates allowed

      this.bind();
      this.filter();
      this.render();
      this.updateStickyOffset();
      this.updateChipArrows();
      this.updateSticky();
      window.addEventListener('resize', () => { this.updateStickyOffset(); this.updateChipArrows(); this.updateSticky(); });
      window.addEventListener('scroll', () => {
        if (this._stickyRaf) return;
        this._stickyRaf = requestAnimationFrame(() => { this._stickyRaf = null; this.updateSticky(); });
      }, { passive: true });
    }

    // Desktop: keep the right-hand summary column pinned as the collection
    // scrolls, using a transform so it works even when an ancestor's overflow
    // or transform would disable CSS position: sticky.
    updateSticky() {
      const side = this.side;
      const layout = this.layout;
      if (!side || !layout) return;
      if (!window.matchMedia('(min-width: 960px)').matches) {
        side.style.transform = '';
        return;
      }
      const pad = 20; // .sbb-layout padding
      const top = 20; // gap from the viewport top
      const rect = layout.getBoundingClientRect();
      const contentHeight = layout.clientHeight - pad * 2;
      const maxTranslate = Math.max(0, contentHeight - side.offsetHeight);
      let t = top - rect.top - pad;
      t = Math.max(0, Math.min(t, maxTranslate));
      side.style.transform = t > 0 ? `translateY(${t}px)` : '';
    }

    // Show each scroll arrow only when there's room to scroll that way.
    updateChipArrows() {
      const el = this.chipsRow;
      if (!el) return;
      const overflow = el.scrollWidth > el.clientWidth + 4;
      const atStart = el.scrollLeft <= 4;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
      if (this.chipPrev) this.chipPrev.hidden = !overflow || atStart;
      if (this.chipNext) this.chipNext.hidden = !overflow || atEnd;
    }

    // On mobile the summary panel is a fixed bottom card; reserve space below
    // the content equal to its collapsed height so nothing hides behind it.
    updateStickyOffset() {
      if (!this.panel) return;
      const mobile = window.matchMedia('(max-width: 959px)').matches;
      if (!mobile) { this.root.style.setProperty('--sbb-panel-pad', '0px'); return; }
      if (!this.panel.classList.contains('is-open')) {
        this.collapsedHeight = this.panel.offsetHeight;
      }
      this.root.style.setProperty('--sbb-panel-pad', (this.collapsedHeight || 0) + 'px');
    }

    bind() {
      if (this.search) {
        this.search.addEventListener('input', (e) => {
          this.query = e.target.value.trim().toLowerCase();
          this.filter();
        });
      }

      if (this.chipsRow) {
        this.chipsRow.addEventListener('click', (e) => {
          const chip = e.target.closest('[data-sbb-chip]');
          if (!chip) return;
          this.category = chip.dataset.sbbChip;
          this.chipsRow.querySelectorAll('[data-sbb-chip]').forEach((c) => {
            c.setAttribute('aria-pressed', c === chip ? 'true' : 'false');
          });
          this.filter();
        });
      }

      if (this.chipsRow) {
        if (this.chipPrev) {
          this.chipPrev.addEventListener('click', () => this.chipsRow.scrollBy({ left: -160, behavior: 'smooth' }));
        }
        if (this.chipNext) {
          this.chipNext.addEventListener('click', () => this.chipsRow.scrollBy({ left: 160, behavior: 'smooth' }));
        }
        this.chipsRow.addEventListener('scroll', () => this.updateChipArrows(), { passive: true });
      }

      this.grid.addEventListener('click', (e) => {
        const card = e.target.closest('[data-sbb-card]');
        if (!card || card.hasAttribute('data-unavailable')) return;
        // Make sure the card reflects the currently chosen variant before we
        // act on it — guards against a missed/late change event so we never
        // add the previously selected variant.
        const select = card.querySelector('[data-sbb-variant]');
        if (select && select.value !== card.dataset.variantId) this.changeVariant(select);
        if (e.target.closest('[data-sbb-add]')) { this.add(card.dataset.variantId); return; }
        if (e.target.closest('[data-sbb-plus]')) { this.add(card.dataset.variantId); return; }
        if (e.target.closest('[data-sbb-minus]')) { this.removeOne(card.dataset.variantId); }
      });

      this.grid.addEventListener('change', (e) => {
        const select = e.target.closest('[data-sbb-variant]');
        if (select) this.changeVariant(select);
      });

      this.tierEls.forEach((tier) => {
        tier.addEventListener('click', () => {
          // Tapping a tier nudges focus to the grid so customers can fill it.
          this.grid.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          if (this.search) this.search.focus({ preventScroll: true });
        });
      });

      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', () => { this.picks = []; this.render(); });
      }

      this.purchaseOpts.forEach((opt) => {
        opt.addEventListener('click', () => {
          this.purchaseType = opt.dataset.sbbPurchaseOpt;
          this.purchaseOpts.forEach((o) => o.setAttribute('aria-pressed', o === opt ? 'true' : 'false'));
          this.renderFooter();
        });
      });

      this.cta.addEventListener('click', () => this.addToCart());

      if (this.peekToggle) {
        this.peekToggle.addEventListener('click', () => {
          const open = this.panel.classList.toggle('is-open');
          this.peekToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          this.updateStickyOffset();
        });
      }

      this.accordionTriggers.forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const panel = trigger.nextElementSibling;
          const expanded = trigger.getAttribute('aria-expanded') === 'true';
          trigger.setAttribute('aria-expanded', expanded ? 'false' : 'true');
          if (panel) panel.hidden = expanded;
        });
      });
    }

    findCard(variantId) {
      return this.cards.find((c) => c.dataset.variantId === variantId);
    }

    // Switch a card to a different variant: update its id/price/image and
    // re-render. Any variant already in the bundle stays put (shown in the
    // picks row), so changing the dropdown lets you add the new variant too.
    changeVariant(select) {
      const card = select.closest('[data-sbb-card]');
      if (!card) return;
      const opt = select.options[select.selectedIndex];
      const newId = select.value;
      const newPrice = (opt && opt.dataset.price) || card.dataset.price;

      card.dataset.variantId = newId;
      card.dataset.price = newPrice;
      if (opt && opt.dataset.image) card.dataset.image = opt.dataset.image;

      // Swap the product card image to match the selected variant.
      if (opt && opt.dataset.cardImage) {
        const mediaImg = card.querySelector('.sbb-card__media img');
        if (mediaImg) {
          mediaImg.srcset = '';
          mediaImg.src = opt.dataset.cardImage;
        }
      }

      const priceEl = card.querySelector('[data-sbb-card-price]');
      if (priceEl) priceEl.textContent = this.money(parseInt(newPrice, 10) || 0);

      this.render();
    }

    countOf(variantId) {
      return this.picks.filter((id) => id === variantId).length;
    }

    add(variantId) {
      if (this.picks.length >= this.maxSize) {
        this.showToast(`That's the most you can add — ${this.maxSize} per bundle.`);
        return;
      }
      this.picks.push(variantId);
      this.render();
    }

    removeOne(variantId) {
      const i = this.picks.lastIndexOf(variantId);
      if (i < 0) return;
      this.picks.splice(i, 1);
      this.render();
    }

    removeAt(index) {
      this.picks.splice(index, 1);
      this.render();
    }

    filter() {
      let visible = 0;
      this.cards.forEach((card) => {
        const name = (card.dataset.name || '').toLowerCase();
        const cat = card.dataset.category || '';
        const matchesCategory = this.category === 'All' || cat === this.category;
        const matchesQuery = !this.query || name.includes(this.query);
        const show = matchesCategory && matchesQuery;
        card.hidden = !show;
        if (show) visible += 1;
      });
      this.empty.classList.toggle('is-visible', visible === 0);
      const label = visible === 1 ? 'product' : 'products';
      const suffix = this.category !== 'All' ? ` · ${this.category}` : '';
      if (this.countline) this.countline.textContent = `${visible} ${label}${suffix}`;
    }

    // Largest tier whose size <= count; null if below the minimum.
    tierForCount(count) {
      let match = null;
      this.tiers.forEach((t) => { if (count >= t.size) match = t; });
      return match;
    }

    // Returns { price, full, savings } in cents for a given count.
    priceForCount(count) {
      const full = this.picks.reduce((sum, id) => {
        const card = this.findCard(id);
        return sum + (card ? parseInt(card.dataset.price, 10) || 0 : 0);
      }, 0);
      const tier = this.tierForCount(count);
      if (!tier) return { price: full, full, savings: 0, locked: true };
      const savings = Math.max(0, full - tier.price);
      return { price: tier.price, full, savings, locked: false };
    }

    applySubscription(cents) {
      if (this.purchaseType !== 'subscribe' || !this.subscribeDiscount) return cents;
      return Math.round(cents * (1 - this.subscribeDiscount / 100));
    }

    renderCards() {
      this.cards.forEach((card) => {
        const count = this.countOf(card.dataset.variantId);
        const selected = count > 0;
        card.classList.toggle('is-selected', selected);
        const countEl = card.querySelector('[data-sbb-count]');
        if (countEl) countEl.textContent = count;
        const plus = card.querySelector('[data-sbb-plus]');
        if (plus) plus.disabled = this.picks.length >= this.maxSize;
      });
    }

    renderTiers() {
      const count = this.picks.length;
      const activeTier = this.tierForCount(count);
      this.tierEls.forEach((el) => {
        const size = parseInt(el.dataset.size, 10);
        const unlocked = count >= size;
        el.classList.toggle('is-unlocked', unlocked);
        el.classList.toggle('is-active', activeTier ? size === activeTier.size : false);
        const hint = el.querySelector('[data-sbb-tier-hint]');
        if (hint) {
          if (!unlocked && size > count) {
            const need = size - count;
            hint.textContent = `+${need} to unlock`;
          } else {
            hint.textContent = '';
          }
        }
      });
    }

    renderProgress() {
      const count = this.picks.length;
      const pct = this.maxSize ? Math.min(100, (count / this.maxSize) * 100) : 0;
      if (this.progressFill) this.progressFill.style.width = `${pct}%`;
      if (this.peekCount) {
        const word = count === 1 ? 'item' : 'items';
        this.peekCount.textContent = `${count} of ${this.maxSize} ${word}`;
      }
      // Small "Add to cart" CTA on the collapsed sticky cart, once the first
      // goal is reached. Tapping it expands the panel (handled by the peek).
      if (this.peekCta) this.peekCta.hidden = count < this.minSize;

      if (!this.progressLabel) return;
      if (count === 0) {
        const t0 = this.tiers[0];
        this.progressLabel.innerHTML = t0
          ? `<strong class="sbb-progress__hl">Add ${t0.size}</strong> to unlock ${t0.size} for ${this.money(t0.price)}`
          : '';
      } else if (count < this.minSize) {
        const t0 = this.tiers[0];
        const need = this.minSize - count;
        this.progressLabel.innerHTML = `<strong class="sbb-progress__hl">Add ${need} more</strong> → ${t0.size} for ${this.money(t0.price)} 🔥`;
      } else if (count < this.maxSize) {
        const next = this.tiers.find((t) => t.size > count);
        if (next) {
          const need = next.size - count;
          this.progressLabel.innerHTML = `<strong class="sbb-progress__hl">Add ${need} more</strong> → ${next.size} for ${this.money(next.price)} — even better value`;
        } else {
          this.progressLabel.innerHTML = `Bundle ready — <strong>${count} items</strong> 🎉`;
        }
      } else {
        this.progressLabel.innerHTML = `<strong>Best value unlocked!</strong> ${count} items 🎉`;
      }
    }

    renderTray() {
      const count = this.picks.length;
      this.traySlots.forEach((slot, i) => {
        const variantId = this.picks[i];
        if (variantId) {
          const card = this.findCard(variantId);
          const img = card && card.dataset.image;
          slot.classList.add('is-filled');
          slot.style.backgroundImage = img ? `url('${img}')` : '';
          slot.textContent = img ? '' : (i + 1);
        } else {
          slot.classList.remove('is-filled');
          slot.style.backgroundImage = '';
          slot.textContent = i + 1;
        }
      });
      void count;
    }

    renderFooter() {
      const count = this.picks.length;
      const { price, full, locked } = this.priceForCount(count);
      const displayPrice = locked ? price : this.applySubscription(price);
      const displaySavings = locked ? 0 : (full - displayPrice);

      // Picks row
      this.picksEl.innerHTML = '';
      if (count === 0) {
        this.placeholders.forEach((ph) => this.picksEl.appendChild(ph));
      } else {
        this.picks.forEach((variantId, index) => {
          const card = this.findCard(variantId);
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'sbb-pick';
          button.setAttribute('aria-label', `Remove ${card ? card.dataset.name : 'item'}`);
          const img = document.createElement('span');
          img.className = 'sbb-pick__img';
          if (card && card.dataset.image) img.style.backgroundImage = `url('${card.dataset.image}')`;
          button.appendChild(img);
          const remove = document.createElement('span');
          remove.className = 'sbb-pick__remove';
          remove.innerHTML = SVG_X;
          button.appendChild(remove);
          button.addEventListener('click', () => this.removeAt(index));
          this.picksEl.appendChild(button);
        });
      }

      // Price
      this.priceEl.textContent = this.money(count === 0 ? 0 : displayPrice);
      this.priceEl.classList.toggle('has-savings', displaySavings > 0);
      if (displaySavings > 0) {
        this.fullPriceEl.textContent = this.money(full);
        this.fullPriceEl.style.display = '';
        this.saveEl.textContent = `Save ${this.money(displaySavings)}`;
        this.saveEl.style.display = '';
      } else {
        this.fullPriceEl.style.display = 'none';
        this.saveEl.style.display = 'none';
      }

      // CTA
      const ready = count >= this.minSize;
      this.cta.disabled = !ready;
      if (!ready) {
        const need = Math.max(0, this.minSize - count);
        this.cta.textContent = count === 0
          ? (this.cta.dataset.emptyLabel || 'Add to build your bundle')
          : `Add ${need} more to build your bundle`;
      } else {
        const verb = this.purchaseType === 'subscribe' ? 'Subscribe' : 'Add bundle';
        this.cta.textContent = `${verb} · ${this.money(displayPrice)}`;
      }

      if (this.clearBtn) this.clearBtn.hidden = count === 0;
    }

    render() {
      this.renderCards();
      this.renderTiers();
      this.renderProgress();
      this.renderTray();
      this.renderFooter();
      this.updateStickyOffset();
      this.updateSticky();
    }

    showToast(message) {
      this.toastInner.textContent = message;
      this.toast.classList.add('is-visible');
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => this.toast.classList.remove('is-visible'), 2400);
    }

    addToCart() {
      if (this.picks.length < this.minSize || this.cta.disabled) return;
      const subscribe = this.purchaseType === 'subscribe';

      const grouped = {};
      this.picks.forEach((id) => {
        const card = this.findCard(id);
        const sellingPlan = subscribe && card ? card.dataset.sellingPlan : '';
        const key = `${id}::${sellingPlan || ''}`;
        if (!grouped[key]) grouped[key] = { id: parseInt(id, 10), quantity: 0, sellingPlan };
        grouped[key].quantity += 1;
      });
      const items = Object.values(grouped).map((g) => {
        const item = { id: g.id, quantity: g.quantity };
        if (g.sellingPlan) item.selling_plan = parseInt(g.sellingPlan, 10);
        return item;
      });

      this.cta.disabled = true;
      const originalLabel = this.cta.textContent;
      this.cta.textContent = 'Adding…';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      })
        .then((response) => {
          if (!response.ok) return response.json().then((err) => Promise.reject(err));
          return response.json();
        })
        .then((data) => {
          this.picks = [];
          this.render();
          this.showToast('Bundle added to your bag! 💕');
          document.dispatchEvent(new CustomEvent('sbb:added-to-cart', { detail: data, bubbles: true }));
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        })
        .catch((err) => {
          const message = (err && err.description) || 'Something went wrong — please try again.';
          this.showToast(message);
          this.cta.disabled = false;
          this.cta.textContent = originalLabel;
        });
    }
  }

  function init() {
    document.querySelectorAll('[data-skincare-bundle-builder]').forEach((root) => {
      if (root.__sbbInitialised) return;
      root.__sbbInitialised = true;
      new SkincareBundleBuilder(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();